export const mrpEn = {
  title: 'Material requirements planning',
  subtitle:
    'Time-phased demand, supply and non-binding action proposals with a reproducible source snapshot.',
  help: { open: 'Open help for material requirements planning' },
  tabs: { planning: 'Planning', history: 'Run history', policies: 'Planning policies' },
  run: {
    title: 'Create planning run',
    description:
      'Choose the planning date, horizon and optional product scope. Running MRP never creates orders automatically.',
  },
  fields: {
    asOf: 'Planning date',
    horizon: 'Horizon end',
    productScope: 'Product scope',
    strategy: 'Sourcing strategy',
    safetyStock: 'Safety stock',
    procurementLead: 'Procurement lead time (days)',
    productionLead: 'Production lead time (days)',
    productionMinimum: 'Minimum production quantity',
    productionLot: 'Production lot size',
    note: 'Reason / note',
  },
  allProducts: 'All stock-managed products',
  actions: {
    run: 'Run MRP',
    'convert-purchase': 'Create draft purchase order',
    'convert-production': 'Create draft production order',
    dismiss: 'Dismiss proposal',
  },
  messages: {
    invalidInput: 'Check the planning input.',
    runCreated: 'The immutable MRP snapshot was created.',
    policySaved: 'Planning policy saved.',
    'convert-purchase': 'Draft purchase order created explicitly.',
    'convert-production': 'Draft production order created explicitly.',
    dismiss: 'Proposal dismissed with reason.',
  },
  stats: {
    products: 'Products',
    shortages: 'Shortages',
    purchase: 'Purchase proposals',
    production: 'Production proposals',
    manual: 'Manual review',
    warnings: 'Warnings',
  },
  columns: {
    product: 'Product',
    gross: 'Gross demand',
    available: 'Available stock',
    reserved: 'Reserved',
    receipts: 'Expected receipts',
    net: 'Net requirement',
    due: 'Requirement date',
    action: 'Action proposal',
    date: 'Date',
    event: 'Event',
    quantity: 'Quantity',
    formula: 'Calculation',
    run: 'Run',
    created: 'Created',
  },
  empty: {
    noRunTitle: 'No planning result selected',
    noRun: 'Create a run or select an immutable result from the history.',
  },
  snapshot: 'Source hash {{sourceHash}} · result hash {{resultHash}}',
  details: {
    timeline: 'Calculation trace',
    timelineDescription:
      'Supplies are processed before demands on the same date; every planned receipt restores the target buffer.',
    sources: 'Demand and supply sources',
    sourcesDescription:
      'Every quantity links back to the document, production order or stock snapshot used by this run.',
  },
  events: {
    OPENING: 'Opening stock',
    DEMAND: 'Demand',
    SUPPLY: 'Receipt',
    PLANNED_RECEIPT: 'Proposed receipt',
  },
  sourceTypes: {
    SALES_ORDER: 'Customer order',
    PRODUCTION_MATERIAL: 'Planned production material',
    PLANNED_PRODUCTION_EXPLOSION: 'Exploded recipe demand',
    STOCK: 'Eligible stock snapshot',
    PURCHASE_ORDER: 'Open purchase order',
    PRODUCTION_ORDER: 'Planned production receipt',
  },
  suggestion: { none: 'No shortage' },
  statuses: { OPEN: 'Open', CONVERTED: 'Converted', DISMISSED: 'Dismissed' },
  strategies: {
    AUTO: 'Automatic rule',
    BUY: 'Purchase',
    MAKE: 'Produce',
    BUY_OR_MAKE: 'Purchase or produce',
    NONE: 'Manual review only',
  },
  policy: {
    title: 'Product planning policies',
    description:
      'Thresholds remain in the product master; these settings add sourcing, lead-time and lot-sizing rules.',
    dialogTitle: 'Edit planning policy',
    dialogDescription: 'Values are version-checked to prevent parallel overwrites.',
  },
  decision: {
    'convert-purchase': {
      title: 'Create purchase order draft?',
      description:
        'This explicit action creates one draft order from the frozen proposal. It does not send or confirm the order.',
      confirm: 'I confirm creation of the draft purchase order.',
    },
    'convert-production': {
      title: 'Create production order draft?',
      description:
        'This explicit action creates one draft from the approved recipe and routing captured by the proposal.',
      confirm: 'I confirm creation of the draft production order.',
    },
    dismiss: {
      title: 'Dismiss action proposal?',
      description:
        'The shortage remains visible in the immutable run; your reason is recorded in the audit trail.',
      confirm: 'I confirm dismissal of this proposal.',
    },
  },
  warningTitle: 'Planning warning',
  warnings: {
    MRP_BATCH_STOCK_RECONCILIATION:
      'The eligible lot total differs from the product stock balance.',
    MRP_BATCH_SHELF_LIFE_MISSING:
      'At least one lot lacks required shelf-life data and was excluded.',
    MRP_INCOMPATIBLE_UNIT: 'A source quantity uses an incompatible unit and was excluded.',
    MRP_SUPPLIER_MISSING: 'No active approved supplier assignment is available.',
    MRP_APPROVED_RECIPE_OR_ROUTING_MISSING:
      'No matching approved recipe and routing are available.',
    MRP_PROCUREMENT_DISABLED: 'Automatic sourcing is disabled for this product.',
    MRP_RELEASE_DATE_OVERDUE:
      'The calculated release date is before the planning date; immediate review is required.',
  },
} as const;
